import { ElementRef, inject, Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom } from '@ngrx/operators';
import { map, Observable, tap } from 'rxjs';

const defaultState = {
  modalRef: undefined,
  isProcessing: false,
};

interface GlobalState {
  modalRef: NgbModalRef;
  isProcessing: boolean;
}

@Injectable()
export class GlobalStore extends ComponentStore<GlobalState> {
  private readonly modalService = inject(NgbModal);

  public readonly modalRef$: Observable<NgbModalRef> = this.select(state => state.modalRef);

  public readonly isProcessing$: Observable<boolean> = this.select(state => state.isProcessing);

  public constructor() {
    super(defaultState);
  }

  public readonly openModal = this.effect((elementRef$: Observable<ElementRef>) =>
    elementRef$.pipe(
      tap(() => this.closeModal()),
      map(elementRef =>
        this.modalService.open(elementRef, {
          centered: true,
          backdrop: 'static',
          keyboard: false,
          size: 'xl',
        }),
      ),
      tap((modalRef: NgbModalRef) => this.patchState({ modalRef })),
    ),
  );

  public readonly closeModal = this.effect(trigger$ =>
    trigger$.pipe(
      concatLatestFrom(() => [this.modalRef$]),
      tap(([, modalRef]) => {
        if (modalRef) {
          modalRef.close();
        }
      }),
      tap(() => this.patchState({ modalRef: undefined })),
    ),
  );

  public readonly setProcessing = this.effect((isProcessing$: Observable<boolean>) =>
    isProcessing$.pipe(tap((isProcessing: boolean) => this.patchState({ isProcessing }))),
  );
}
